// src/components/SoftPushPromptModal.jsx
//
// Friendly pre-permission "soft ask" shown after a meaningful action (list a
// pedal, add a want, make an offer, open an offer waiting on you). Tapping "Turn on notifications" triggers
// the real OS prompt; "Not now" snoozes it. Mounted once at the app root and
// driven by services/softPushPrompt. Native-only (never surfaces on web).

import React, { useEffect, useRef, useState } from 'react';
import {
  subscribeToSoftPushPrompt,
  acceptSoftPushPrompt,
  dismissSoftPushPrompt,
} from '../services/softPushPrompt';

export const REASON_COPY = {
  add_want: 'Want added! Get notified the moment a match for your Wantlist is listed.',
  make_offer: 'Offer sent! Turn on notifications so you know the moment they respond.',
  list_pedal: 'Pedal listed! Turn on notifications so you know the moment you get an offer.',
  received_offer: "You've got an offer! Turn on notifications so you never miss the next one.",
  default: 'Stay up to date on offers and matches. Turn on notifications.',
};

export default function SoftPushPromptModal() {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('default');
  const [busy, setBusy] = useState(false);
  // Synchronous mirror of `busy` plus the service's finish() for this
  // opening. finish() releases the one-at-a-time lock and tells the trigger
  // (e.g. the listing banner) that the pop-up has closed.
  const busyRef = useRef(false);
  const finishRef = useRef(null);
  // The running "Turn on" attempt. Closing the pop-up and finishing the
  // permission attempt are separate: unmounting mid-attempt hides the
  // pop-up but keeps the automatic-prompt lock until the attempt settles.
  const attemptRef = useRef(null);

  useEffect(() => {
    const unsubscribe = subscribeToSoftPushPrompt(({ reason: r, finish } = {}) => {
      finishRef.current = finish || null;
      setReason(r || 'default');
      setOpen(true);
    });
    return () => {
      unsubscribe();
      // Unmounted while open: never strand the lock, but don't release it
      // before a running permission attempt has finished.
      const finish = finishRef.current;
      finishRef.current = null;
      if (!finish) return;
      if (attemptRef.current) attemptRef.current.then(finish, finish);
      else finish();
    };
  }, []);

  const close = () => {
    setOpen(false);
    const finish = finishRef.current;
    finishRef.current = null;
    if (finish) finish();
  };

  if (!open) return null;

  const handleEnable = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    const attempt = acceptSoftPushPrompt();
    attemptRef.current = attempt;
    try {
      await attempt;
    } finally {
      attemptRef.current = null;
      busyRef.current = false;
      setBusy(false);
      close();
    }
  };

  // Backdrop taps and "Not now" are ignored while "Turn on" is running, so a
  // stray tap can't close the pop-up and write a snooze mid-request.
  const handleDismiss = () => {
    if (busyRef.current) return;
    dismissSoftPushPrompt();
    close();
  };

  const message = REASON_COPY[reason] || REASON_COPY.default;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.45)',
        padding: '1rem',
        paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
      }}
      onClick={handleDismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '18px',
          maxWidth: '420px',
          width: '100%',
          padding: '1.75rem 1.5rem 1.5rem',
          boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
          fontFamily: 'Nunito, sans-serif',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#EAF3FA',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
          }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#5A9ECB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </div>

        <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#4A5759', margin: '0 0 0.5rem' }}>
          Turn on notifications
        </h2>
        <p style={{ fontSize: '0.95rem', color: '#6b7280', lineHeight: 1.5, margin: '0 0 1.5rem' }}>
          {message}
        </p>

        <button
          type="button"
          onClick={handleEnable}
          disabled={busy}
          style={{
            display: 'block',
            width: '100%',
            border: 'none',
            borderRadius: '9999px',
            padding: '13px 20px',
            fontSize: '1rem',
            fontWeight: 700,
            color: '#ffffff',
            backgroundColor: '#5A9ECB',
            cursor: busy ? 'default' : 'pointer',
            opacity: busy ? 0.7 : 1,
            fontFamily: 'Nunito, sans-serif',
            marginBottom: '0.6rem',
          }}
        >
          {busy ? 'Enabling…' : 'Turn on notifications'}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={busy}
          style={{
            display: 'block',
            width: '100%',
            border: 'none',
            background: 'transparent',
            padding: '10px',
            fontSize: '0.95rem',
            fontWeight: 600,
            color: '#9ca3af',
            cursor: 'pointer',
            fontFamily: 'Nunito, sans-serif',
          }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
